import React from "react";
import { Col, Input, Label, Row, TabPane } from "reactstrap";

export type PersonsImportWizardTabFileUploadProps = {
  activeTab: number;
  toggleTab: (tab: number) => void;
  selectedFile: File | null;
  isDragActive: boolean;
  setIsDragActive: React.Dispatch<React.SetStateAction<boolean>>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  openFilePicker: () => void;
  handlePickedFiles: (files: FileList | File[]) => void;
  firstLineIncludesFieldNames: boolean;
  setFirstLineIncludesFieldNames: React.Dispatch<React.SetStateAction<boolean>>;
  formatFileSize: (bytes: number) => string;
  onStartOver: () => void;
  csvParseError: string | null;
  csvRowCount: number;
};

const PersonsImportWizardTabFileUpload: React.FC<PersonsImportWizardTabFileUploadProps> = ({
  activeTab, toggleTab, selectedFile, isDragActive, setIsDragActive,
  fileInputRef, openFilePicker, handlePickedFiles, firstLineIncludesFieldNames,
  setFirstLineIncludesFieldNames, formatFileSize, onStartOver, csvParseError, csvRowCount,
}) => {
  const fileExt = selectedFile?.name.split(".").pop()?.toUpperCase() ?? "";

  return (
    <TabPane tabId={1}>
      <div className="d-flex align-items-start gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid #eef0f4" }}>
        <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, background: "#eef2ff" }}>
          <i className="ri-upload-cloud-2-line" style={{ fontSize: 18, color: "#4361ee" }} />
        </div>
        <div className="flex-grow-1">
          <h5 className="mb-0 fw-semibold">File Upload</h5>
          <p className="text-muted mb-0 mt-1">Upload a CSV or XLSX file containing your persons data.</p>
        </div>
        <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">Step 1 of 4</span>
      </div>

      <Row>
        <Col lg={12}>
          <div
            role="button" tabIndex={0}
            onClick={openFilePicker}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openFilePicker(); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(true); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(false); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(false); if (e.dataTransfer?.files?.length) handlePickedFiles(e.dataTransfer.files); }}
            className="w-100 d-flex flex-column align-items-center justify-content-center"
            style={{
              minHeight: 280,
              background: isDragActive ? "#f0f4ff" : selectedFile && !csvParseError ? "#f8fff8" : "#f8f9fc",
              border: `2px dashed ${isDragActive ? "#4361ee" : selectedFile && !csvParseError ? "#22c55e" : csvParseError ? "#ef4444" : "#d1d9e6"}`,
              borderRadius: 10, cursor: "pointer", userSelect: "none", outline: "none",
              padding: "32px 24px", transition: "border-color 0.18s ease, background 0.18s ease",
            }}
          >
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }}
              onChange={(e) => { if (e.target.files?.length) handlePickedFiles(e.target.files); e.target.value = ""; }} />

            {selectedFile ? (
              <>
                <div className="d-flex align-items-center justify-content-center rounded-2 mb-3 flex-shrink-0" style={{ width: 56, height: 56, background: csvParseError ? "#fee2e2" : "#dcfce7" }}>
                  {csvParseError
                    ? <i className="ri-error-warning-line" style={{ fontSize: 28, color: "#ef4444" }} />
                    : <i className="ri-file-text-line" style={{ fontSize: 28, color: "#16a34a" }} />}
                </div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="fw-semibold" style={{ fontSize: 15, maxWidth: 340, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#1e293b" }} title={selectedFile.name}>{selectedFile.name}</span>
                  {fileExt && <span className="badge rounded-pill" style={{ background: "#94a3b8", color: "#fff", fontSize: 11, fontWeight: 600, letterSpacing: 0.3 }}>{fileExt}</span>}
                </div>
                <div className="d-flex align-items-center gap-2 text-muted mb-3">
                  <span>{formatFileSize(selectedFile.size)}</span>
                  {!csvParseError && csvRowCount > 0 && (<><span style={{ color: "#cbd5e1" }}>·</span><span><strong className="text-body">{csvRowCount.toLocaleString()}</strong> rows detected</span></>)}
                </div>
                <div className="form-check mb-3" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                  <Input id="persons-firstLine" type="checkbox" className="form-check-input" checked={firstLineIncludesFieldNames} onChange={(e) => setFirstLineIncludesFieldNames(e.target.checked)} />
                  <Label className="form-check-label" htmlFor="persons-firstLine" style={{ color: "#64748b" }}>First line includes field names</Label>
                </div>
                <div className="d-flex align-items-center gap-1" style={{ fontSize: 13, color: "#94a3b8" }}>
                  <i className="ri-refresh-line" /><span>Click anywhere to replace file</span>
                </div>
              </>
            ) : (
              <>
                <div className="d-flex align-items-center justify-content-center rounded-circle mb-4" style={{ width: 72, height: 72, background: isDragActive ? "#dbeafe" : "#f1f5f9" }}>
                  <i className="ri-upload-cloud-2-line" style={{ fontSize: 34, color: isDragActive ? "#3b82f6" : "#64748b" }} />
                </div>
                <div className="fw-semibold mb-1" style={{ fontSize: 16, color: "#64748b" }}>{isDragActive ? "Drop your file here" : "Drag & drop your file here"}</div>
                <div style={{ fontSize: 14, color: "#64748b" }}>or <span style={{ textDecoration: "underline" }}>click to browse</span></div>
                <div className="d-flex align-items-center gap-2 mt-4">
                  {["CSV", "XLSX"].map((fmt) => (
                    <span key={fmt} style={{ background: "#94a3b8", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 4, letterSpacing: 0.4 }}>{fmt}</span>
                  ))}
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>

      {csvParseError && (
        <div className="d-flex align-items-start gap-2 rounded-2 mt-3 px-3 py-2" style={{ background: "#fee2e2", border: "1px solid #fca5a5" }}>
          <i className="ri-error-warning-line text-danger flex-shrink-0 mt-1" />
          <span style={{ color: "#dc2626" }}>{csvParseError}</span>
        </div>
      )}

      <div className="d-flex align-items-center gap-3 mt-4 pt-3" style={{ borderTop: "1px solid #eef0f4" }}>
        <button type="button" className="btn btn-light btn-label" onClick={onStartOver}>
          <i className="ri-restart-line label-icon align-middle fs-16 me-2" />Start Over
        </button>
        <button type="button" className="btn btn-primary btn-label right ms-auto"
          disabled={!selectedFile || !!csvParseError || csvRowCount === 0}
          onClick={() => toggleTab(activeTab + 1)}>
          <i className="ri-git-merge-line label-icon align-middle fs-16 ms-2" />Proceed to Field Mapping
        </button>
      </div>
    </TabPane>
  );
};

export default PersonsImportWizardTabFileUpload;
